import { useState, useEffect } from 'react';
import { statisticsApi } from '../../services/api';

interface Stat {
  number: string;
  label: string;
}

const NumbersSection = () => {
  const [stats, setStats] = useState<Stat[]>([
    { number: '13', label: 'Nomination Categories' },
    { number: '240k', label: 'Nominations' },
    { number: '500k', label: 'Votes Casted' },
    { number: '432', label: 'Winners' }
  ]);
  const [loading, setLoading] = useState(true);

  // YouTube video ID
  const youtubeVideoId = 'Fawf7FJifEo'; // Tanzania Digital Awards video

  // Helper function to format numbers
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    }
    return num.toString();
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await statisticsApi.getAll();
        
        // Update stats with real data from API
        setStats([
          { number: data.categories.toString(), label: 'Nomination Categories' },
          { number: formatNumber(data.nominations), label: 'Nominations' },
          { number: formatNumber(data.votes), label: 'Votes Casted' },
          { number: data.winners.toString(), label: 'Winners' }
        ]);
      } catch (error) {
        console.error('Failed to fetch statistics:', error);
        // Keep default values if fetch fails
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return (
    <section 
      className="py-16 bg-blue-900" 
      style={{ 
        backgroundColor: '#0f1b3c',
        padding: '4rem 0'
      }}
    >
      <div 
        className="container mx-auto px-4" 
        style={{ 
          maxWidth: '1400px',
          padding: '0 2rem'
        }}
      >
        {/* Header Text */}
        <div 
          className="text-center mb-12" 
          style={{ 
            marginBottom: '3rem',
            padding: '0 2rem'
          }}
        >
          <h2 
            className="text-4xl md:text-5xl font-bold text-white mb-0 uppercase leading-tight" 
            style={{ 
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: '700', 
              color: 'white',
              marginBottom: '0',
              letterSpacing: '0.05em',
              lineHeight: '1.3'
            }}
          >
            DIGITAL EXCELLENCE & INNOVATION.
          </h2>
        </div>

        {/* YouTube Video */}
        <div 
          className="mb-16" 
          style={{ 
            marginBottom: '4rem',
            maxWidth: '1200px',
            margin: '0 auto 4rem auto'
          }}
        >
          <div 
            className="relative w-full overflow-hidden rounded-2xl shadow-2xl"
            style={{ 
              position: 'relative',
              width: '100%',
              paddingBottom: '56.25%', // 16:9 aspect ratio
              overflow: 'hidden',
              borderRadius: '1rem',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }}
          >
            <iframe
              src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&mute=1&loop=1&playlist=${youtubeVideoId}&controls=1&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
              title="Tanzania Digital Awards Highlights"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none'
              }}
            />
          </div>
        </div>

        {/* Stats Grid */}
        <div 
          className="grid grid-cols-2 md:grid-cols-4 gap-8"
          style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '3rem',
            maxWidth: '1200px',
            margin: '0 auto'
          }}
        >
          {stats.map((stat, index) => (
            <div 
              key={index} 
              className="text-center group relative"
              style={{
                textAlign: 'center',
                position: 'relative'
              }}
            >
              {/* Vertical separator line (except for last item) */}
              {index < stats.length - 1 && (
                <div 
                  className="hidden md:block absolute right-0 top-1/2 transform -translate-y-1/2 w-px h-24 bg-white bg-opacity-20"
                  style={{
                    position: 'absolute',
                    right: '-1.5rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '1px',
                    height: '6rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)'
                  }}
                ></div>
              )}
              
              {/* Number */}
              <h3 
                className="text-6xl md:text-7xl font-bold mb-4 text-white" 
                style={{ 
                  fontSize: 'clamp(3rem, 5vw, 5rem)',
                  fontWeight: '700',
                  color: 'white',
                  marginBottom: '1rem',
                  lineHeight: '1'
                }}
              >
                {stat.number}
              </h3>
              
              {/* Label */}
              <p 
                className="text-base md:text-lg font-normal text-white text-opacity-90" 
                style={{ 
                  fontSize: 'clamp(0.875rem, 1.5vw, 1.125rem)',
                  fontWeight: '400',
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: '1.5'
                }}
              >
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NumbersSection;
